import {
  celebrationService,
  type CelebrationWorkflowMappingError,
  type CelebrationWorkflowMetrics,
  type CreateCelebrationOrchestrationPayload,
} from '@/lib/api/celebration.service';
import type { CreateCelebrationDto } from '@/types/celebration.types';
import type { ChatEventSuggestionDto } from '@om/shared-types';

export interface CelebrationCreateCelebrantSeed {
  name: string;
  role?: string;
  email?: string;
  userId?: number;
  isSurprise?: boolean;
}

export interface CelebrationEventSelections {
  selectedEvents?: ChatEventSuggestionDto[];
  selectedActivities?: Record<string, string[]>;
  selectedGoalTemplateIdsByEvent?: Record<string, number[]>;
  addServicesToShortlist?: boolean;
  selectedServiceDefinitionIds?: number[];
}

export interface CreateCelebrationWorkflowInput {
  celebrationData: CreateCelebrationDto;
  celebrants?: CelebrationCreateCelebrantSeed[];
  paragraphContent?: string;
  confirmedTags?: Record<string, string[]>;
  chatSessionId?: string;
  eventSelections?: CelebrationEventSelections;
}

export interface CreateCelebrationWorkflowResult {
  celebrationId: number;
  warnings: string[];
  mappingErrors: CreateCelebrationWorkflowMappingError[];
  metrics: CreateCelebrationWorkflowMetrics;
  createdEventIdsBySource: Record<string, number>;
  logId?: string;
}

export type CreateCelebrationWorkflowMappingError = CelebrationWorkflowMappingError;

export type CreateCelebrationWorkflowMetrics = CelebrationWorkflowMetrics;

const toIsoDateForBackend = (value: string | undefined): string | undefined => {
  if (!value || !value.trim()) {
    return undefined;
  }

  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${trimmed}T00:00:00.000Z`;
  }

  const parsed = new Date(trimmed);
  // Keep non-empty invalid strings so backend validation can return a precise error.
  return Number.isNaN(parsed.getTime()) ? trimmed : parsed.toISOString();
};

const normalizeActivityTitles = (titles: string[] | undefined): string[] => {
  if (!Array.isArray(titles)) {
    return [];
  }

  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const title of titles) {
    const trimmed = String(title).trim();
    if (!trimmed) continue;
    if (seen.has(trimmed.toLowerCase())) continue;
    seen.add(trimmed.toLowerCase());
    normalized.push(trimmed);
  }

  return normalized;
};

const normalizeSelectedActivities = (
  selectedActivities?: Record<string, string[]>,
): Record<string, string[]> | undefined => {
  if (!selectedActivities) {
    return undefined;
  }

  const normalizedEntries = Object.entries(selectedActivities)
    .map(([eventId, activities]) => [eventId, normalizeActivityTitles(activities)] as const)
    .filter(([, activities]) => activities.length > 0);

  if (normalizedEntries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(normalizedEntries);
};

const normalizeSelectedGoalTemplateIdsByEvent = (
  selectedGoalTemplateIdsByEvent?: Record<string, number[]>,
): Record<string, number[]> | undefined => {
  if (!selectedGoalTemplateIdsByEvent) {
    return undefined;
  }

  const normalizedEntries = Object.entries(selectedGoalTemplateIdsByEvent)
    .map(([eventId, goalTemplateIds]) => {
      const normalizedGoalTemplateIds = Array.isArray(goalTemplateIds)
        ? goalTemplateIds.filter((goalTemplateId) => Number.isFinite(goalTemplateId))
        : [];
      return [eventId, normalizedGoalTemplateIds] as const;
    })
    .filter(([, goalTemplateIds]) => goalTemplateIds.length > 0);

  if (normalizedEntries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(normalizedEntries);
};

const normalizeSelectedServiceDefinitionIds = (
  selectedServiceDefinitionIds?: number[],
): number[] | undefined => {
  if (!Array.isArray(selectedServiceDefinitionIds)) {
    return undefined;
  }

  const deduped = Array.from(new Set(selectedServiceDefinitionIds.filter((serviceDefinitionId) => Number.isFinite(serviceDefinitionId))));
  return deduped.length > 0 ? deduped : undefined;
};

type CelebrationOrchestrationFields = Pick<
  CreateCelebrationOrchestrationPayload,
  'celebrants' | 'celebrant_names' | 'celebrant_roles' | 'celebrant_emails'
>;

const buildCelebrationOrchestrationFields = (
  celebrants: CelebrationCreateCelebrantSeed[] | undefined,
): CelebrationOrchestrationFields => {
  if (!Array.isArray(celebrants) || celebrants.length === 0) {
    return {};
  }

  const normalizedCelebrants = celebrants.map((celebrant, index) => {
    const name = celebrant.name?.trim() || `Celebrant ${index + 1}`;
    const role = celebrant.role?.trim() || 'Celebrant';
    const email = celebrant.email?.trim();
    const userId = typeof celebrant.userId === 'number' && celebrant.userId > 0
      ? celebrant.userId
      : undefined;

    return {
      name,
      role,
      email,
      userId,
      isSurprise: Boolean(celebrant.isSurprise),
    };
  });

  const allHaveUserIds = normalizedCelebrants.every((celebrant) => typeof celebrant.userId === 'number');
  if (allHaveUserIds) {
    return {
      celebrants: normalizedCelebrants.map((celebrant) => ({
        user_id: celebrant.userId as number,
        role: celebrant.role,
        is_surprise: celebrant.isSurprise,
      })),
    };
  }

  const hasAnyCelebrantEmail = normalizedCelebrants.some((celebrant) => Boolean(celebrant.email));

  return {
    celebrant_names: normalizedCelebrants.map((celebrant) => celebrant.name),
    celebrant_roles: normalizedCelebrants.map((celebrant) => celebrant.role),
    // Preserve index alignment with names/roles. Missing emails stay as empty
    // strings and are handled server-side without placeholder synthesis.
    celebrant_emails: hasAnyCelebrantEmail
      ? normalizedCelebrants.map((celebrant) => celebrant.email || '')
      : undefined,
  };
};

const buildOrchestrationPayload = (
  input: CreateCelebrationWorkflowInput,
): CreateCelebrationOrchestrationPayload => {
  const celebrationData: Omit<CreateCelebrationDto, 'celebrants'> = {
    ...input.celebrationData,
  };

  const submissionData: Omit<CreateCelebrationDto, 'celebrants'> = {
    ...celebrationData,
    start_date: toIsoDateForBackend(celebrationData.start_date),
    end_date: toIsoDateForBackend(celebrationData.end_date),
  };

  const payload: CreateCelebrationOrchestrationPayload = {
    ...submissionData,
  };

  const paragraphContent = input.paragraphContent?.trim();
  if (paragraphContent) {
    payload.paragraph_content = paragraphContent;
  }

  const chatSessionId = input.chatSessionId?.trim();
  if (chatSessionId) {
    payload.chat_session_id = chatSessionId;
  }

  if (input.confirmedTags && Object.keys(input.confirmedTags).length > 0) {
    payload.confirmed_tags = input.confirmedTags;
  }

  const selectedEvents = input.eventSelections?.selectedEvents ?? [];
  if (selectedEvents.length > 0) {
    payload.selected_events = selectedEvents;
  }

  const selectedActivities = normalizeSelectedActivities(input.eventSelections?.selectedActivities);
  if (selectedActivities) {
    payload.selected_activities = selectedActivities;
  }

  const selectedGoalTemplateIdsByEvent = normalizeSelectedGoalTemplateIdsByEvent(
    input.eventSelections?.selectedGoalTemplateIdsByEvent,
  );
  if (selectedGoalTemplateIdsByEvent) {
    payload.selected_goal_template_ids_by_event = selectedGoalTemplateIdsByEvent;
  }

  const selectedServiceDefinitionIds = normalizeSelectedServiceDefinitionIds(
    input.eventSelections?.selectedServiceDefinitionIds,
  );
  if (selectedServiceDefinitionIds) {
    payload.selected_service_definition_ids = selectedServiceDefinitionIds;
    payload.add_services_to_shortlist = input.eventSelections?.addServicesToShortlist ?? true;
  } else if (input.eventSelections?.addServicesToShortlist === false) {
    payload.add_services_to_shortlist = false;
  }

  return {
    ...payload,
    ...buildCelebrationOrchestrationFields(input.celebrants),
  };
};

export const createCelebrationWithWorkflow = async (
  input: CreateCelebrationWorkflowInput,
): Promise<CreateCelebrationWorkflowResult> => {
  const payload = buildOrchestrationPayload(input);
  const result = await celebrationService.createCelebrationWithOrchestration(payload);

  return {
    celebrationId: result.celebration.id,
    warnings: result.warnings,
    mappingErrors: result.mappingErrors,
    metrics: result.metrics,
    logId: result.logId || result.requestId,
    // Frontend callers still inspect this key, but event-id mapping is now
    // owned by backend orchestration and not returned by this endpoint.
    createdEventIdsBySource: {},
  };
};
